import React, { useState } from "react";
import { copyText } from "@/lib/clipboard";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, LogIn, Building2, Eye, Pencil, ChevronDown, Ban, CheckCircle2,
  RefreshCw, CalendarPlus, KeyRound, Copy, Trash2, AlertTriangle,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/kit/StatCard";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { resolveAsset } from "@/components/kit/FileUpload";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtNumber, fmtDate } from "@/lib/format";
import { OrganizationDrawer } from "@/drawers/OrganizationDrawer";
import { TwoFactorAdminControl } from "@/components/settings/AccountSecurity";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Row({ k, v }) {
  return <div className="flex items-center justify-between gap-3 border-b border-white/[0.05] py-1.5 last:border-0"><span className="text-muted-foreground">{k}</span><span className="truncate text-right text-foreground/90">{v}</span></div>;
}
const userCols = [
  { key: "tenant_seq", label: "ID", render: (r) => <span className="text-muted-foreground">#{r.tenant_seq ?? "—"}</span> },
  { key: "name", label: "Name", sortable: true, exportValue: (r) => r.name, render: (r) => <div><div className="text-foreground/90">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></div> },
  { key: "telegram", label: "Contact", render: (r) => <span className="text-foreground/80">{r.telegram || r.phone || "—"}</span> },
  { key: "role", label: "Role", sortable: true, render: (r) => <span className="rounded bg-white/[0.05] px-2 py-0.5 text-xs capitalize text-foreground/90">{(r.role || "").replace("_", " ")}</span> },
  { key: "balance", label: "Balance", align: "right", mono: true, exportValue: (r) => r.balance, render: (r) => fmtMoney(r.balance) },
  { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
  { key: "last_login", label: "Last login", sortable: true, render: (r) => <span className="text-muted-foreground">{r.last_login ? fmtDate(r.last_login) : "—"}</span> },
  { key: "created_at", label: "Created", render: (r) => <span className="text-muted-foreground">{r.created_at ? fmtDate(r.created_at) : "—"}</span> },
];

const advertiserCols = [
  { key: "tenant_seq", label: "ID", render: (r) => <span className="text-muted-foreground">#{r.tenant_seq ?? "—"}</span> },
  { key: "name", label: "Advertiser", sortable: true, render: (r) => <div className="text-foreground/90">{r.name}</div> },
  { key: "contact_person", label: "Contact", render: (r) => <div><div className="text-foreground/80">{r.contact_person || "—"}</div><div className="text-xs text-muted-foreground">{r.email || ""}</div></div> },
  { key: "telegram", label: "Telegram / Phone", render: (r) => <span className="text-foreground/80">{r.telegram || r.phone || "—"}</span> },
  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  { key: "active_offers", label: "Offers", align: "right", render: (r) => <span className="text-foreground/90">{r.active_offers}/{r.total_offers}</span> },
  { key: "conversions", label: "Conversions", align: "right", mono: true, render: (r) => fmtNumber(r.conversions) },
  { key: "revenue", label: "Revenue", align: "right", mono: true, exportValue: (r) => r.revenue, render: (r) => <span className="text-[#22C55E]">{fmtMoney(r.revenue)}</span> },
  { key: "last_activity", label: "Last activity", render: (r) => <span className="text-muted-foreground">{r.last_activity ? fmtDate(r.last_activity) : "—"}</span> },
];

function ReadOnlyTab({ tid, endpoint, columns, params = {}, emptyText, filters, searchKeys, exportName }) {
  const { data, isLoading } = useQuery({
    queryKey: ["org-list", tid, endpoint, params],
    queryFn: async () => (await http.get(endpoint, { params: { tenant_id: tid, ...params } })).data,
  });
  return <DataTablePro testId="org-table" columns={columns} rows={data?.items || []} loading={isLoading}
    emptyText={emptyText || "No records."} filters={filters} searchKeys={searchKeys} exportName={exportName || "export"} />;
}

function Overview({ ov }) {
  if (!ov) return null;
  const c = ov.counts || {}; const p = ov.portal || {}; const tech = ov.technical || {}; const sub = ov.subscription;
  const act = ov.activity || {}; const org = ov.organization || {};
  const cards = [
    { label: "Users", value: fmtNumber(c.users) }, { label: "Advertisers", value: fmtNumber(c.advertisers) },
    { label: "Offers", value: fmtNumber(c.offers) }, { label: "Streams", value: fmtNumber(c.streams) },
    { label: "Clicks today", value: fmtNumber(act.clicks_today) }, { label: "Conversions today", value: fmtNumber(act.conversions_today) },
    { label: "Revenue", value: fmtMoney(act.revenue_total) }, { label: "Wallet balance", value: fmtMoney(ov.balance_total) },
  ];
  const domains = p.custom_domains || [];
  return (
    <div className="space-y-5" data-testid="org-overview">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">{cards.map((x) => <StatCard key={x.label} label={x.label} value={x.value} />)}</div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5 lg:col-span-2" data-testid="org-block-portal">
          <h3 className="mb-3 text-sm font-medium text-foreground">Portal &amp; endpoints</h3>
          <div className="space-y-2 text-sm">
            <Row k="Affiliate Portal" v={<code className="text-[#22C55E]">{p.portal_url}</code>} />
            <Row k="Tracking base" v={<code className="text-[#EAB308]">{p.tracking_url}</code>} />
            <Row k="Postback URL" v={<code className="text-[#A855F7]">{p.postback_url || "Platform default"}</code>} />
            <Row k="Registration" v={p.registration_enabled ? "Open" : "Closed"} />
          </div>
          <div className="mt-4">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Custom domains ({domains.length})</h4>
            {domains.length ? (
              <div className="space-y-1.5">
                {domains.map((d) => (
                  <div key={d.domain} className="flex items-center justify-between gap-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2">
                    <span className="truncate font-mono text-xs text-foreground/90">{d.domain}</span>
                    <span className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">{(d.usages || []).join(" · ")}</span>
                      <StatusBadge status={d.status} />
                    </span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-muted-foreground">No custom domains connected.</p>}
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="org-block-subscription">
            <h3 className="mb-3 text-sm font-medium text-foreground">Subscription</h3>
            {sub ? <div className="space-y-2 text-sm">
              <Row k="Plan" v={sub.plan_name || "—"} />
              <Row k="Status" v={<StatusBadge status={sub.status} />} />
              <Row k="Ends" v={sub.current_period_end ? fmtDate(sub.current_period_end) : "—"} />
              <Row k="Days left" v={sub.days_left} />
            </div> : <p className="text-sm text-muted-foreground">No subscription.</p>}
          </div>
          <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="org-block-technical">
            <h3 className="mb-3 text-sm font-medium text-foreground">Technical</h3>
            <div className="space-y-2 text-sm">
              <Row k="Active domains" v={fmtNumber(tech.active_domains)} />
              <Row k="Webhooks" v={fmtNumber(tech.webhooks)} />
              <Row k="SSL" v={tech.ssl} />
              <Row k="Last activity" v={act.last_activity ? fmtDate(act.last_activity) : "—"} />
              <Row k="Platform version" v={tech.platform_version} />
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid="org-block-organization">
        <h3 className="mb-3 text-sm font-medium text-foreground">Organization</h3>
        <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          <Row k="Partner network" v={org.partner_network_name || ov.tenant.name} />
          <Row k="Website" v={org.website || "—"} />
          <Row k="Contact email" v={org.contact_email || ov.owner?.email || "—"} />
          <Row k="Country" v={org.country || "—"} />
          <Row k="Owner" v={ov.owner ? `${ov.owner.name}` : "—"} />
          <Row k="Created" v={fmtDate(ov.tenant.createdAt)} />
          <Row k="Currency" v={org.default_currency || "—"} />
        </div>
        {ov.owner?.id && (
          <div className="mt-5 border-t border-white/[0.06] pt-5">
            <TwoFactorAdminControl userId={ov.owner.id} userName={ov.owner.name || ov.owner.email} />
          </div>
        )}
      </div>
    </div>
  );
}

function AdvertisersTab({ tid }) {
  const { data, isLoading } = useQuery({
    queryKey: ["org-advertisers", tid],
    queryFn: async () => (await http.get(`/saas/organizations/${tid}/advertisers`)).data,
  });
  return <DataTablePro testId="org-table" columns={advertiserCols} rows={data?.items || []} loading={isLoading}
    emptyText="No advertisers." searchKeys={["name", "contact_person", "email"]} exportName="advertisers"
    filters={[{ key: "status", label: "Status", getValue: (r) => r.status, options: [{ value: "active", label: "Active" }, { value: "paused", label: "Paused" }] }]} />;
}

export default function OrganizationDetail() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { impersonate } = useAuth();
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // {type}
  const { data: ov } = useQuery({ queryKey: ["org-overview", tid], queryFn: async () => (await http.get(`/saas/organizations/${tid}/overview`)).data });
  const { data: plans } = useQuery({ queryKey: ["billing-plans"], queryFn: async () => (await http.get("/billing/settings")).data });

  const refresh = () => qc.invalidateQueries({ queryKey: ["org-overview", tid] });
  const loginAs = async () => {
    try { await impersonate(tid, "tenant_admin"); toast.success("Impersonating Tenant Admin"); navigate("/"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doAction = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const status = ov?.tenant?.status;
  const tabs = [
    { id: "overview", label: "Overview", render: () => <Overview ov={ov} /> },
    { id: "users", label: "Users", render: () => <ReadOnlyTab tid={tid} endpoint="/users" columns={userCols} searchKeys={["name", "email"]} exportName="users"
        filters={[{ key: "role", label: "Role", getValue: (r) => r.role, options: [{ value: "tenant_admin", label: "Tenant Admin" }, { value: "manager", label: "Manager" }, { value: "buyer", label: "Buyer" }] }]} /> },
    { id: "advertisers", label: "Advertisers", render: () => <AdvertisersTab tid={tid} /> },
    { id: "settings", label: "Settings", render: () => <OrgSettingsView tid={tid} ov={ov} /> },
  ];
  const current = tabs.find((t) => t.id === tab) || tabs[0];

  return (
    <div className="space-y-5" data-testid="organization-detail">
      <button onClick={() => navigate("/organizations")} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="org-back-btn"><ArrowLeft className="h-3.5 w-3.5" /> All organizations</button>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/[0.06]"><Building2 className="h-5 w-5 text-muted-foreground" /></span>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{ov?.organization?.partner_network_name || ov?.tenant?.name || "Organization"}</h1>
            <p className="text-sm text-muted-foreground">#{ov?.tenant?.publicId} · {ov?.tenant?.slug} · <StatusBadge status={status} /> · <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> read-only</span></p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="org-edit-btn" className="gap-2 border-white/[0.12]"><Pencil className="h-4 w-4" /> Edit</Button>
          <Button onClick={loginAs} data-testid="org-login-as-btn" className="gap-2 bg-[#EAB308] text-black hover:bg-[#EAB308]/90"><LogIn className="h-4 w-4" /> Login as Tenant Admin</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1 border-white/[0.12]" data-testid="org-actions-btn">Actions <ChevronDown className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-white/[0.1] bg-[#1a1a1e]">
              {status === "suspended"
                ? <DropdownMenuItem onClick={() => doAction(() => http.post(`/saas/organizations/${tid}/activate`), "Activated")} data-testid="org-activate" className="cursor-pointer gap-2"><CheckCircle2 className="h-4 w-4" /> Activate tenant</DropdownMenuItem>
                : <DropdownMenuItem onClick={() => doAction(() => http.post(`/saas/organizations/${tid}/suspend`), "Suspended")} data-testid="org-suspend" className="cursor-pointer gap-2 text-[#EF4444] focus:text-[#EF4444]"><Ban className="h-4 w-4" /> Suspend tenant</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => setDialog({ type: "plan" })} data-testid="org-change-plan" className="cursor-pointer gap-2"><RefreshCw className="h-4 w-4" /> Change plan</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDialog({ type: "extend" })} data-testid="org-extend" className="cursor-pointer gap-2"><CalendarPlus className="h-4 w-4" /> Extend subscription</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDialog({ type: "reset" })} data-testid="org-reset-pw" className="cursor-pointer gap-2"><KeyRound className="h-4 w-4" /> Reset admin password</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDialog({ type: "harddelete" })} data-testid="org-hard-delete" className="cursor-pointer gap-2 text-[#EF4444] focus:text-[#EF4444]"><Trash2 className="h-4 w-4" /> Hard delete tenant</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="no-scrollbar flex flex-wrap gap-1 overflow-x-auto border-b border-white/[0.08]">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`org-tab-${t.id}`}
            className={cn("relative whitespace-nowrap px-3 py-2 text-sm transition-colors", tab === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
            {t.label}{tab === t.id && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-white" />}
          </button>
        ))}
      </div>
      <div data-testid={`org-panel-${current.id}`}>{current.render()}</div>

      <OrganizationDrawer open={editOpen} item={ov?.tenant ? { internalId: tid, name: ov.tenant.name, slug: ov.tenant.slug, status: ov.tenant.status, plan: ov.tenant.plan, defaultCurrency: ov.tenant.defaultCurrency, allow_portal_domain: !!ov.tenant.allow_portal_domain, notes: ov.tenant.notes } : null} orgInfo={ov?.organization} branding={ov?.branding} onOpenChange={setEditOpen} />
      <ActionDialog dialog={dialog} tid={tid} tenantName={ov?.tenant?.name} plans={plans?.plans || []} onClose={() => setDialog(null)} onDone={refresh} />
    </div>
  );
}

function ActionDialog({ dialog, tid, tenantName, plans, onClose, onDone }) {
  const navigate = useNavigate();
  const [planId, setPlanId] = useState("");
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  if (!dialog) return null;
  const close = () => { setPw(null); setConfirmName(""); onClose(); };
  const run = async (fn, msg) => { setBusy(true); try { await fn(); if (msg) toast.success(msg); onDone(); if (dialog.type !== "reset") close(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="border-white/[0.1] bg-[#141417]" data-testid="org-action-dialog">
        {dialog.type === "plan" && <>
          <DialogHeader><DialogTitle>Change subscription plan</DialogTitle></DialogHeader>
          <Select value={planId} onValueChange={setPlanId}>
            <SelectTrigger className="border-white/[0.12] bg-[#0A0A0A]" data-testid="org-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
            <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.price} ({p.period})</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button disabled={!planId || busy} onClick={() => run(() => http.post(`/saas/organizations/${tid}/change-plan`, { plan_id: planId }), "Plan changed")} data-testid="org-plan-save">Change plan</Button></DialogFooter>
        </>}
        {dialog.type === "extend" && <>
          <DialogHeader><DialogTitle>Extend subscription</DialogTitle></DialogHeader>
          <label className="text-xs text-muted-foreground">Extend by days</label>
          <input type="number" value={days} onChange={(e) => setDays(e.target.value)} data-testid="org-extend-days" className="rounded-lg border border-white/[0.12] bg-[#0A0A0A] px-3 py-2 text-sm text-foreground" />
          <DialogFooter><Button disabled={busy} onClick={() => run(() => http.post(`/saas/organizations/${tid}/extend`, { days: Number(days) }), `Extended by ${days} days`)} data-testid="org-extend-save">Extend</Button></DialogFooter>
        </>}
        {dialog.type === "reset" && <>
          <DialogHeader><DialogTitle>Reset Tenant Admin password</DialogTitle></DialogHeader>
          {pw ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Temporary password for <span className="text-foreground">{pw.email}</span>. Copy & share it securely — it won't be shown again.</p>
              <div className="flex items-center gap-2 rounded-lg border border-white/[0.12] bg-[#0A0A0A] px-3 py-2">
                <code className="flex-1 font-mono text-sm text-[#22C55E]" data-testid="org-reset-result">{pw.temporary_password}</code>
                <button onClick={() => copyText(pw.temporary_password)}><Copy className="h-4 w-4 text-muted-foreground" /></button>
              </div>
            </div>
          ) : <p className="text-sm text-muted-foreground">Generate a new temporary password for the tenant admin. The current password will stop working.</p>}
          <DialogFooter>
            {pw ? <Button onClick={close} data-testid="org-reset-close">Done</Button>
              : <Button disabled={busy} onClick={() => run(async () => { const r = await http.post(`/saas/organizations/${tid}/reset-admin-password`); setPw(r.data); }, "Password reset")} data-testid="org-reset-confirm" className="bg-[#EF4444] hover:bg-[#EF4444]/90">Reset password</Button>}
          </DialogFooter>
        </>}
        {dialog.type === "harddelete" && <>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#EF4444]"><AlertTriangle className="h-5 w-5" /> Hard delete tenant</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-[#EF4444]/30 bg-[#EF4444]/[0.08] px-4 py-3 text-sm text-[#EF4444]">
              This permanently deletes <span className="font-semibold">{tenantName}</span> and ALL related data — users, advertisers, offers, streams, clicks, conversions, wallets, finance, domains, logs, API keys, webhooks. This cannot be undone.
            </div>
            <label className="text-xs text-muted-foreground">Type the tenant name <span className="text-foreground">{tenantName}</span> to confirm</label>
            <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={tenantName} data-testid="org-harddelete-confirm" className="w-full rounded-lg border border-white/[0.12] bg-[#0A0A0A] px-3 py-2 text-sm text-foreground" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} className="border-white/[0.12]">Cancel</Button>
            <Button disabled={busy || confirmName.trim().toLowerCase() !== (tenantName || "").trim().toLowerCase()}
              onClick={() => run(async () => { const r = await http.post(`/saas/organizations/${tid}/hard-delete`, { confirm_name: confirmName }); toast.success(`Deleted ${r.data.total_deleted} records`); navigate("/organizations"); }, null)}
              data-testid="org-harddelete-confirm-btn" className="bg-[#EF4444] hover:bg-[#EF4444]/90">Permanently delete</Button>
          </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
}

function OrgSettingsView({ tid, ov }) {
  const { data } = useQuery({ queryKey: ["org-settings", tid], queryFn: async () => (await http.get("/tenant-settings", { params: { tenant_id: tid } })).data });
  const s = data?.sections || {};
  const org = ov?.organization || s.organization || {};
  const br = ov?.branding || s.branding || {};
  const reg = s.registration || {};
  const logo = resolveAsset(br.logo_url);
  return (
    <div className="space-y-4" data-testid="org-settings-view">
      <p className="text-xs text-muted-foreground">All organization data below is owned and edited by the tenant — Super Admin sees it live (single source of truth).</p>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5 lg:col-span-2">
          <h3 className="mb-3 text-sm font-medium text-foreground">Organization information</h3>
          <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            <Row k="Partner network name" v={org.partner_network_name || "—"} />
            <Row k="Legal name" v={org.legal_name || "—"} />
            <Row k="Contact email" v={org.contact_email || "—"} />
            <Row k="Support email" v={org.support_email || "—"} />
            <Row k="Website" v={org.website || "—"} />
            <Row k="Country" v={org.country || "—"} />
            <Row k="Telegram" v={org.telegram || "—"} />
            <Row k="WhatsApp" v={org.whatsapp || "—"} />
            <Row k="Phone" v={org.phone || "—"} />
            <Row k="LinkedIn" v={org.linkedin || "—"} />
            <Row k="Currency" v={org.default_currency || "—"} />
            <Row k="Language" v={org.default_language || "—"} />
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5">
            <h3 className="mb-3 text-sm font-medium text-foreground">Branding</h3>
            <div className="mb-3 flex h-16 items-center justify-center rounded-lg border border-white/[0.06] bg-[#0A0A0A]">
              {logo ? <img src={logo} alt="logo" className="h-full w-auto object-contain p-2" /> : <span className="text-xs text-muted-foreground">No logo</span>}
            </div>
            <div className="space-y-2 text-sm">
              <Row k="Login title" v={br.login_title || "—"} />
              <Row k="Registration title" v={br.registration_title || "—"} />
              <Row k="Footer" v={br.footer_text || "—"} />
            </div>
          </div>
          <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5">
            <h3 className="mb-3 text-sm font-medium text-foreground">Registration</h3>
            <div className="space-y-2 text-sm">
              <Row k="Enabled" v={reg.registration_enabled ? "Yes" : "No"} />
              <Row k="Public" v={reg.allow_public_registration ? "Yes" : "No"} />
              <Row k="Manual approval" v={reg.manual_approval ? "Yes" : "No"} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
